import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("fsm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * Auth + effective-permissions provider.
 *
 * `permissions` is an object of shape `{ [module]: string[] }` mirroring what
 * the backend computes in `_get_effective_permissions()` (defaults + role
 * template overrides + user-level overrides). `can(module, action)` is the
 * canonical read-side check; admin always short-circuits to true.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/permissions/me`);
      setPermissions(data?.permissions || {});
    } catch {
      setPermissions({});
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("fsm_token");
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then(async (res) => {
        setUser(res.data);
        await loadPermissions();
      })
      .catch(() => localStorage.removeItem("fsm_token"))
      .finally(() => setLoading(false));
  }, [loadPermissions]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("fsm_token", data.access_token);
    setUser(data.user);
    await loadPermissions();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("fsm_token");
    setUser(null);
    setPermissions({});
  };

  const can = useCallback(
    (module, action = "view") => {
      if (!user) return false;
      if (user.role === "admin") return true;
      const acts = permissions?.[module];
      return Array.isArray(acts) && acts.includes(action);
    },
    [user, permissions]
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, permissions, can, reloadPermissions: loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};
